import { apiFetch } from '../../services/http';

export type Listing = {
  id: string;
  sellerAlias: string;
  title: string;
  description: string;
  priceCents: number;
  currency: string;
  media: string[];
  durationMinutes?: number; // Service duration in minutes
  workingDays?: string[]; // Days of week: ["monday", "tuesday", ...]
  workStartTime?: string; // Start time: "09:00"
  workEndTime?: string; // End time: "18:00"
  discussionThreadId?: string; // Forum thread ID for service discussion
  discussionThreadTitle?: string; // Cached forum thread title
  reviews?: Review[]; // First 5 buyer-to-seller reviews (embedded)
  history?: ListingStatusHistory[]; // Status change history (embedded)
  status: string; // draft, pending_approval, approved, rejected
  // Contact methods
  whatsapp?: string; // WhatsApp phone number
  wechat?: string; // WeChat ID
  telegram?: string; // Telegram username
  phoneNumber?: string; // General phone number
  createdAt: string;
  updatedAt: string;
};

export type Booking = {
  id: string;
  listingId: string;
  buyerAlias: string;
  status: string;
  scheduledFor: string;
  createdAt: string;
  updatedAt: string;
  verificationRef: string;
  buyerReview?: Review; // Buyer's review of seller (embedded)
  sellerReview?: Review; // Seller's review of buyer (embedded)
};

export type VerificationEvent = {
  bookingId: string;
  event: string;
  timestamp: string;
};

export type BookingStatusHistory = {
  id: number;
  bookingId: string;
  status: string;
  action: string; // Human-readable description
  actor: string; // Who made the change
  createdAt: string;
};

export type ListingStatusHistory = {
  id: number;
  listingId: string;
  status: string;
  adminComment?: string;
  adminUserId?: string;
  createdAt: string;
};

export type Review = {
  id: string;
  bookingId: string;
  listingId: string;
  buyerAlias?: string;  // Set when buyer reviews seller
  sellerAlias?: string; // Set when seller reviews buyer
  reviewType: 'buyer_to_seller' | 'seller_to_buyer';
  rating: number; // 1-5 stars
  comment: string;
  classification?: string;
  approved?: boolean;
  createdAt: string;
};

export type ReviewStats = {
  buyerAlias: string;
  total: number;
  normal: number;
  observable: number;
  advance: number;
  superior: number;
  advanceSharePerm: boolean;
};

export async function fetchListings(limit = 50): Promise<Listing[]> {
  return apiFetch<Listing[]>(`/api/marketplace/listings?limit=${limit}`);
}

export async function fetchListing(listingId: string): Promise<Listing> {
  return apiFetch<Listing>(`/api/marketplace/listings/${listingId}`);
}


type ListingPayload = Partial<Listing> & { mediaFiles?: File[]; adminComment?: string };

function buildListingFormData(payload: ListingPayload): FormData {
  const formData = new FormData();
  if (payload.sellerAlias) formData.append('sellerAlias', payload.sellerAlias);
  if (payload.title) formData.append('title', payload.title);
  if (payload.description) formData.append('description', payload.description);
  if (payload.priceCents !== undefined) formData.append('priceCents', String(payload.priceCents));
  if (payload.currency) formData.append('currency', payload.currency);
  if (payload.durationMinutes !== undefined && payload.durationMinutes !== null) {
    formData.append('durationMinutes', String(payload.durationMinutes));
  }
  if (payload.workingDays) {
    payload.workingDays.forEach((day) => formData.append('workingDays', day));
  }
  if (payload.workStartTime) formData.append('workStartTime', payload.workStartTime);
  if (payload.workEndTime) formData.append('workEndTime', payload.workEndTime);
  if (payload.discussionThreadId) formData.append('discussionThreadId', payload.discussionThreadId);
  if (payload.discussionThreadTitle) formData.append('discussionThreadTitle', payload.discussionThreadTitle);
  if (payload.status) formData.append('status', payload.status);
  if (payload.adminComment) formData.append('adminComment', payload.adminComment);

  const media = (payload.media || []).filter((m) => !m.startsWith('data:'));
  media.forEach((m) => formData.append('media', m));
  (payload.mediaFiles || []).forEach((file) => formData.append('media', file));

  return formData;
}

export async function createListing(payload: ListingPayload): Promise<Listing> {
  if (payload.mediaFiles && payload.mediaFiles.length > 0) {
    const formData = buildListingFormData(payload);
    return apiFetch<Listing>(`/api/marketplace/listings`, {
      method: 'POST',
      body: formData,
    });
  }

  const media = (payload.media || []).filter((m) => !m.startsWith('data:'));
  return apiFetch<Listing>(`/api/marketplace/listings`, {
    method: 'POST',
    body: JSON.stringify({ ...payload, media }),
  });
}

export async function updateListing(listingId: string, payload: ListingPayload): Promise<Listing> {
  if (payload.mediaFiles && payload.mediaFiles.length > 0) {
    const formData = buildListingFormData(payload);
    return apiFetch<Listing>(`/api/marketplace/listings/${listingId}`, {
      method: 'PUT',
      body: formData,
    });
  }

  const media = (payload.media || []).filter((m) => !m.startsWith('data:'));
  return apiFetch<Listing>(`/api/marketplace/listings/${listingId}`, {
    method: 'PUT',
    body: JSON.stringify({ ...payload, media }),
  });
}

export async function updateListingStatus(
  listingId: string,
  status: string,
  adminComment?: string
): Promise<Listing> {
  return apiFetch<Listing>(`/api/marketplace/listings/${listingId}`, {
    method: 'PUT',
    body: JSON.stringify({ status, adminComment }),
  });
}

export async function fetchListingHistory(listingId: string): Promise<ListingStatusHistory[]> {
  return apiFetch<ListingStatusHistory[]>(`/api/marketplace/listings/${listingId}/history`);
}

export async function createBooking(payload: { listingId: string; buyerAlias: string; scheduledFor?: string }): Promise<Booking> {
  return apiFetch<Booking>(`/api/marketplace/bookings`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateBookingStatus(bookingId: string, status: string): Promise<Booking> {
  return apiFetch<Booking>(`/api/marketplace/bookings/${bookingId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function fetchSellerBookings(alias: string): Promise<Booking[]> {
  return apiFetch<Booking[]>(`/api/marketplace/bookings/seller?alias=${encodeURIComponent(alias)}`);
}

export async function fetchBuyerBookings(alias: string): Promise<Booking[]> {
  return apiFetch<Booking[]>(`/api/marketplace/bookings/buyer?alias=${encodeURIComponent(alias)}`);
}

export async function addVerificationEvent(bookingId: string, event: string): Promise<void> {
  await apiFetch(`/api/marketplace/bookings/${bookingId}/verify`, {
    method: 'POST',
    body: JSON.stringify({ event }),
  });
}

export async function fetchVerificationEvents(bookingId: string): Promise<VerificationEvent[]> {
  return apiFetch<VerificationEvent[]>(`/api/marketplace/bookings/${bookingId}/verify`);
}

export async function fetchBookingHistory(bookingId: string): Promise<BookingStatusHistory[]> {
  return apiFetch<BookingStatusHistory[]>(`/api/marketplace/bookings/${bookingId}/history`);
}

// Review moderation
export async function fetchPendingReviews(limit = 50): Promise<Review[]> {
  return apiFetch<Review[]>(`/api/marketplace/admin/reviews/pending?limit=${limit}`);
}

export async function approveReview(reviewId: string, classification: string): Promise<Review> {
  return apiFetch<Review>(`/api/marketplace/admin/reviews/${reviewId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ classification }),
  });
}

export async function fetchReviewStats(): Promise<ReviewStats> {
  return apiFetch<ReviewStats>(`/api/marketplace/reviews/stats`);
}

// Seller alias management
export type SellerAliasResponse = {
  registered: boolean;
  alias?: {
    userId: string;
    sellerAlias: string;
    createdAt: string;
    updatedAt: string;
  };
  message?: string;
};

export async function registerSellerAlias(sellerAlias: string): Promise<SellerAliasResponse> {
  return apiFetch<SellerAliasResponse>(`/api/marketplace/seller/register`, {
    method: 'POST',
    body: JSON.stringify({ sellerAlias }),
  });
}

export async function getMySellerAlias(): Promise<SellerAliasResponse> {
  return apiFetch<SellerAliasResponse>(`/api/marketplace/seller/me`);
}

// Buyer alias management
export type BuyerAliasResponse = {
  registered: boolean;
  alias?: {
    userId: string;
    buyerAlias: string;
    createdAt: string;
    updatedAt: string;
  };
  message?: string;
};

export async function registerBuyerAlias(buyerAlias: string): Promise<BuyerAliasResponse> {
  return apiFetch<BuyerAliasResponse>(`/api/marketplace/buyer/register`, {
    method: 'POST',
    body: JSON.stringify({ buyerAlias }),
  });
}

export async function getMyBuyerAlias(): Promise<BuyerAliasResponse> {
  return apiFetch<BuyerAliasResponse>(`/api/marketplace/buyer/me`);
}

// Review management
export async function createReview(
  bookingId: string,
  rating: number,
  comment: string,
  reviewType: 'buyer_to_seller' | 'seller_to_buyer',
  alias: string  // buyerAlias or sellerAlias depending on reviewType
): Promise<Review> {
  const body: any = {
    rating,
    comment,
    reviewType,
  };

  if (reviewType === 'buyer_to_seller') {
    body.buyerAlias = alias;
  } else {
    body.sellerAlias = alias;
  }

  return apiFetch<Review>(`/api/marketplace/bookings/${bookingId}/review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function fetchReviewByBooking(
  bookingId: string,
  reviewType: 'buyer_to_seller' | 'seller_to_buyer' = 'buyer_to_seller'
): Promise<Review | null> {
  try {
    return await apiFetch<Review>(
      `/api/marketplace/bookings/${bookingId}/review?reviewType=${reviewType}`
    );
  } catch (err) {
    // Return null if no review found
    return null;
  }
}

export async function fetchListingReviews(listingId: string, limit = 50): Promise<Review[]> {
  return apiFetch<Review[]>(`/api/marketplace/listings/${listingId}/reviews?limit=${limit}`);
}
